import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';

/**
 * Le finestre aperte, dalla più vecchia alla più recente.
 *
 * Una conferma si apre sopra un'altra finestra (rimuovere un gruppo dalla modale "Gruppi
 * assegnati"): Esc deve chiudere solo quella in cima. Ascoltando il document, senza
 * questo elenco, la chiudevano tutte e due insieme.
 */
const openModals: Modal[] = [];

/**
 * Finestra modale.
 *
 * Il contenuto è proiettato: quello marcato `modalActions` finisce nel piè di pagina,
 * il resto nel corpo. La chiusura è sempre un evento verso il chiamante, mai una
 * decisione del componente: chi la usa deve poterla bloccare mentre salva.
 */
@Component({
  selector: 'app-modal',
  template: `
    <div class="backdrop" (click)="requestClose()"></div>
    <div
      #dialog
      class="dialog"
      [class.is-loading]="busy()"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      [attr.aria-label]="title()"
    >
      <header class="dialog-head">
        <div>
          <h2 class="dialog-title">{{ title() }}</h2>
          @if (subtitle() !== null) {
            <p class="dialog-subtitle">{{ subtitle() }}</p>
          }
        </div>
        <button
          type="button"
          class="dialog-close"
          aria-label="Chiudi"
          [disabled]="busy()"
          (click)="requestClose()"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6 6 18M6 6l12 12" stroke-linecap="round" />
          </svg>
        </button>
      </header>

      <div class="dialog-body">
        <ng-content />
      </div>

      <footer class="dialog-actions">
        <ng-content select="[modalActions]" />
      </footer>
    </div>
  `,
  styleUrl: './modal.scss',
})
export class Modal implements AfterViewInit, OnDestroy {
  readonly title = input.required<string>();
  readonly subtitle = input<string | null>(null);
  /** Operazione in corso: la chiusura per Esc o backdrop viene ignorata. */
  readonly busy = input(false);

  readonly closed = output<void>();

  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');
  /**
   * Chi aveva il fuoco prima che la finestra si aprisse: alla chiusura glielo si rende.
   * Senza, il fuoco torna all'inizio della pagina e chi naviga da tastiera deve
   * riattraversare menu e barra dei filtri per tornare alla riga da cui era partito.
   */
  private readonly restoreTo = document.activeElement as HTMLElement | null;

  /**
   * All'apertura il fuoco entra nella finestra.
   *
   * I form marcano da sé il primo campo con `appAutofocus`, ma non sempre funziona: in
   * modifica lo username è disabilitato, e `focus()` su un campo disabilitato non fa
   * nulla — il fuoco restava sul pulsante della riga, dietro alla finestra, e da lì la
   * tabulazione ripartiva dalla pagina coperta. Qui si controlla il risultato invece di
   * darlo per scontato: se nessuno ha preso il fuoco, lo prende il primo controllo utile.
   */
  constructor() {
    openModals.push(this);
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      const dialog = this.dialog().nativeElement;
      if (dialog.contains(document.activeElement)) {
        return;
      }
      (this.focusable()[0] ?? dialog).focus();
    });
  }

  ngOnDestroy(): void {
    openModals.splice(openModals.indexOf(this), 1);
    this.restoreTo?.focus?.();
  }

  private focusable(): HTMLElement[] {
    return [
      ...this.dialog().nativeElement.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => el.offsetParent !== null);
  }

  /**
   * Il fuoco resta dentro la finestra finché è aperta.
   *
   * È una finestra modale dichiarata (`aria-modal`), ma l'attributo da solo non impedisce
   * al Tab di uscire e finire sulla pagina dietro, che intanto è coperta e inerte: si
   * continuerebbe a tabulare fra controlli che non si vedono.
   */
  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.focusable();

    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (openModals[openModals.length - 1] === this) {
      this.requestClose();
    }
  }

  protected requestClose(): void {
    if (!this.busy()) {
      this.closed.emit();
    }
  }
}
